import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import api from '../lib/api';
import { supabase } from '../lib/supabase';

/**
 * VerificationBanner - Shows verification status prominently on dashboard
 * @param {Object} profile - User profile with verification status
 * @param {Function} onVerifyClick - Callback when user clicks verify button
 */
const VerificationBanner = ({ profile, onVerifyClick }) => {
  const { t } = useTranslation();
  const [hasPendingVerification, setHasPendingVerification] = useState(false);
  const [loading, setLoading] = useState(true);

  // PESO-registered employers are vetted by their LGU PESO office through the registration +
  // approval flow — they never self-verify their ID here, so this prompt is not relevant to them.
  const isPesoRegistered =
    (typeof profile?.registration_source === 'string' && profile.registration_source.startsWith('peso_')) ||
    !!profile?.peso_office;

  // Fetch pending verification status
  useEffect(() => {
    const checkPendingVerification = async () => {
      try {
        // FIX: Use Supabase session correctly
        const { data: session } = await supabase.auth.getSession();
        if (!session?.session?.access_token) {
          console.log('VerificationBanner: No active session');
          setLoading(false);
          return;
        }

        console.log('VerificationBanner: Checking verification status for user:', profile?.id);
        const response = await api.get('/api/verification/status', {
          headers: {
            Authorization: `Bearer ${session.session.access_token}`
          }
        });
        
        console.log('VerificationBanner: Status response:', response.data);
        setHasPendingVerification(response.data.status === 'pending');
      } catch (error) {
        console.error('VerificationBanner: Error checking verification status:', error);
        setHasPendingVerification(false);
      } finally {
        setLoading(false);
      }
    };

    if (profile && !profile.verified && !isPesoRegistered) {
      checkPendingVerification();
    } else {
      setLoading(false);
    }
  }, [profile, isPesoRegistered]);

  // Don't show the self-verification banner if already verified, or for PESO-registered
  // employers (their LGU PESO office handles verification through the registration flow).
  if (profile?.verified || isPesoRegistered) {
    return null;
  }

  return (
    <Card className="border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-secondary/5 mb-6">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            {hasPendingVerification ? (
              <div className="rounded-full bg-yellow-100 p-3">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            ) : (
              <div className="rounded-full bg-primary/10 p-3">
                <Shield className="h-6 w-6 text-primary" />
              </div>
            )}
          </div>
          
          <div className="flex-1">
            {hasPendingVerification ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold">{t('verification.idVerificationPending')}</h3>
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                    <Clock className="h-3 w-3 mr-1" />
                    {t('verification.underReview')}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  {t('verification.documentsBeingReviewed')}
                </p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <AlertCircle className="h-4 w-4" />
                  <span>{t('verification.reviewTime')}</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold">{t('verification.verifyYourId')}</h3>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    {t('verification.notVerified')}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  {profile?.role === 'employer' || profile?.role === 'owner' ? (
                    <>{t('verification.verifyAndReceive')}</>
                  ) : (
                    <>{t('verification.verifyToBoost')}</>
                  )}
                </p>
                <Button onClick={onVerifyClick} className="mt-2" size="sm">
                  <Shield className="h-4 w-4 mr-2" />
                  {t('verification.verifyNow')}
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default VerificationBanner;
